import '@styles/features/photos.css';
import { useState, type ReactNode } from 'react';
import { locationIllustration } from '@constants/illustrations';
import { Skeleton } from '@ui';

interface Props {
  /** the photo to show, or null: the location type's illustration on a soft background */
  src: string | null;
  type: string;
  /** names the photo for screen readers */
  name: string;
  /** `cover` fills its box with the photo (panel, header); `thumb` is a small square (list rows) */
  variant?: 'cover' | 'thumb';
  className?: string;
  /** over the picture (a button) */
  children?: ReactNode;
}

/**
 * A location's picture: its cover photo (shimmering until it has loaded, the illustration if it fails), else the
 * illustration of its type. The box keeps its size from CSS (aspect ratio), so nothing moves when the photo arrives.
 */
export function LocationCover({ src, type, name, variant = 'cover', className = '', children }: Props) {
  // remembers which src failed or loaded, so a new src starts over without an effect
  const [state, setState] = useState<{ src: string | null; loaded: boolean; failed: boolean }>({ src, loaded: false, failed: false });
  const s = state.src === src ? state : { src, loaded: false, failed: false };
  const photo = src != null && !s.failed;
  return (
    <div className={`loc-cover ${variant} ${photo ? 'is-photo' : 'is-art'} ${photo && s.loaded ? 'loaded' : ''} ${className}`}>
      {photo ? (
        <>
          {!s.loaded && <Skeleton shape="block" className="loc-cover-sk" />}
          <img
            className="loc-cover-photo"
            src={src}
            // a list row already says which location it is
            alt={variant === 'thumb' ? '' : `Foto ${name}`}
            loading={variant === 'thumb' ? 'lazy' : 'eager'}
            decoding="async"
            onLoad={() => setState({ src, loaded: true, failed: false })}
            onError={() => setState({ src, loaded: false, failed: true })}
          />
        </>
      ) : (
        <img className="loc-cover-art" src={locationIllustration(type)} alt="" loading="lazy" decoding="async" />
      )}
      {children}
    </div>
  );
}
